import { Link } from 'react-router-dom';

interface SpaceStar {
  id: number;
  left: number;
  top: number;
  size: number;
  opacity: number;
  delay: number;
  duration: number;
}

const spaceStars: SpaceStar[] = Array.from({ length: 70 }, (_, id) => ({
  id,
  left: (id * 37) % 100,
  top: (id * 53) % 100,
  size: 1 + (id % 4) * 0.7,
  opacity: 0.35 + (id % 6) * 0.1,
  delay: (id % 9) * 0.35,
  duration: 2.6 + (id % 5) * 0.4,
}));

export function NotFoundPage(): JSX.Element {
  return (
    <section
      className="relative isolate flex min-h-[calc(100vh-8rem)] overflow-hidden bg-slate-950 px-4 py-12 text-white sm:px-6 lg:px-8"
      data-testid="not-found-page"
    >
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(34,211,238,0.18),transparent_28%),radial-gradient(circle_at_82%_72%,rgba(250,204,21,0.14),transparent_26%),linear-gradient(135deg,#020617_0%,#111827_52%,#1e1b4b_100%)]"
        aria-hidden="true"
      />
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {spaceStars.map((star) => (
          <span
            key={star.id}
            className="not-found-star absolute rounded-full bg-white"
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,460px)]">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase text-cyan-200 sm:text-base">
            404 - Unknown sector
          </p>
          <h1 className="mt-4 text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
            Hello, space traveller.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-slate-200">
            Looks like you are lost in space. There is no place like this in the
            entire universe.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/" className="btn-primary inline-flex justify-center px-5 py-3">
              Back to home
            </Link>
            <Link
              to="/create"
              className="inline-flex justify-center rounded-lg border border-white/25 bg-white/10 px-5 py-3 font-medium text-white transition-colors duration-200 hover:bg-white/20"
            >
              Launch a board
            </Link>
          </div>
        </div>

        <div className="relative mx-auto h-[320px] w-full max-w-[460px] sm:h-[420px]" aria-hidden="true">
          <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-200/25 sm:h-80 sm:w-80" />
          <div className="absolute left-1/2 top-1/2 h-44 w-44 -translate-x-1/2 -translate-y-1/2 rounded-full border border-yellow-200/20 sm:h-56 sm:w-56" />

          <div className="absolute bottom-8 left-6 h-28 w-28 rounded-full bg-gradient-to-br from-cyan-300 via-blue-500 to-indigo-700 shadow-2xl shadow-cyan-500/20 sm:h-36 sm:w-36">
            <div className="absolute left-1/2 top-1/2 h-8 w-40 -translate-x-1/2 -translate-y-1/2 rotate-[-18deg] rounded-full border border-yellow-200/70 bg-yellow-200/15 sm:w-52" />
          </div>

          <div className="absolute right-4 top-8 flex h-28 w-28 items-center justify-center rounded-full border border-white/15 bg-slate-900/70 text-4xl font-black text-white shadow-2xl shadow-black/30 sm:h-36 sm:w-36 sm:text-5xl">
            404
          </div>

          <div className="not-found-ship absolute left-[48%] top-[36%] flex h-28 w-28 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
            <img
              src="/retrojet_rounded.svg"
              alt=""
              className="h-20 w-20 rotate-[28deg]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
